// https://github.com/airbnb/react-dates/blob/master/src/components/SingleDatePicker.jsx

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { SingleDatePicker } from 'react-dates';
import dateFormatService from '../../services/DateFormatService';

const returnYears = () => {
  const years = [];
  for (let i = moment().year() - 5; i <= moment().year() + 5; i++) {
    years.push(<option value={i}>{i}</option>);
  }
  return years;
};

const renderMonthElement = ({ month, onMonthSelect, onYearSelect }) => (
  <div style={{ display: 'flex', justifyContent: 'center' }}>
    <div>
      <select
        className="form-control"
        value={month.month()}
        onChange={e => onMonthSelect(month, e.target.value)}
      >
        {moment.months().map((label, value) => (
          <option value={value}>{label}</option>
        ))}
      </select>
    </div>
    <div>
      <select
        className="form-control"
        value={month.year()}
        onChange={e => onYearSelect(month, e.target.value)}
      >
        {returnYears()}
      </select>
    </div>
  </div>
);

const PopupWindowMargin = 8;

class DatePicker extends Component {
  frame = null;

  componentDidMount() {
    const { focused } = this.props;
    if (focused) this.scheduleClamp();
  }

  componentDidUpdate(prevProps) {
    const { focused } = this.props;
    if (focused && !prevProps.focused) this.scheduleClamp();
    if (!focused && prevProps.focused) this.stopClamping();
  }

  componentWillUnmount() {
    this.stopClamping();
  }

  scheduleClamp = () => {
    const { appendToBody } = this.props;
    if (!appendToBody) return;
    this.cancelScheduledClamp();
    this.frame = requestAnimationFrame(() => {
      this.clampPopupIntoWindow();
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.clampPopupIntoWindow();
      });
    });
    window.addEventListener('resize', this.scheduleClamp);
  };

  cancelScheduledClamp = () => {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  };

  stopClamping = () => {
    this.cancelScheduledClamp();
    window.removeEventListener('resize', this.scheduleClamp);
  };

  setPopupMarker = node => {
    this.marker = node;
  };

  renderCalendarInfoWithMarker = () => {
    const { renderCalendarInfo } = this.props;
    return (
      <>
        <span ref={this.setPopupMarker} />
        {renderCalendarInfo ? renderCalendarInfo() : null}
      </>
    );
  };

  findPopup = () => {
    let node = this.marker;
    while (node && node.parentElement && node.parentElement.parentElement !== document.body) {
      node = node.parentElement;
    }
    return node && node.parentElement && node.parentElement.parentElement === document.body
      ? node
      : null;
  };

  clampPopupIntoWindow = () => {
    const popup = this.findPopup();
    if (!popup) return;
    // `margin-left` is ours to use: react-dates writes `left` and `transform` on this element.
    popup.style.marginLeft = '';
    const rect = popup.getBoundingClientRect();
    let shift = 0;
    if (rect.right > window.innerWidth - PopupWindowMargin) {
      shift = window.innerWidth - PopupWindowMargin - rect.right;
    }
    // A calendar wider than the window (or one hanging off the left) lines up with the left edge.
    if (rect.left + shift < PopupWindowMargin) shift = PopupWindowMargin - rect.left;
    if (shift) popup.style.marginLeft = `${Math.round(shift)}px`;
  };

  internalOnDateChange = (date, onDateChange) => {
    const inputValue = document.getElementById(this.props.id)?.value;
    if (inputValue) {
      const parsedInputValue = moment(inputValue, dateFormatService.dateFormat(), true);
      const isDateValid = parsedInputValue.isValid() === true;
      onDateChange(date, isDateValid);
    } else {
      onDateChange(date, true);
    }
  };

  render() {
    const {
      phrases,
      numberOfMonths,
      onDateChange,
      appendToBody,
      renderCalendarInfo,
      ...rest
    } = this.props;
    return (
      <SingleDatePicker
        numberOfMonths={numberOfMonths}
        keepFocusOnInput
        onDateChange={date => this.internalOnDateChange(date, onDateChange)}
        hideKeyboardShortcutsPanel
        renderMonthElement={renderMonthElement} // this method adds a dropdown for both month and year, so that the user doesnt have to navigate to previous years using the arrows
        displayFormat={dateFormatService.dateFormat()} // hardcoded for now, since the EN-culture changes the date format to MM/DD/YYYY and this is not wanted behavior in South-African tenants
        appendToBody={appendToBody}
        renderCalendarInfo={appendToBody ? this.renderCalendarInfoWithMarker : renderCalendarInfo}
        {...rest}
      />
    );
  }
}

DatePicker.propTypes = {
  numberOfMonths: PropTypes.number,
  phrases: PropTypes.shape({}),
  focused: PropTypes.bool,
  appendToBody: PropTypes.bool,
  renderCalendarInfo: PropTypes.func,
};
DatePicker.defaultProps = {
  phrases: {},
  numberOfMonths: 1,
  focused: false,
  appendToBody: false,
  renderCalendarInfo: undefined,
};

export default DatePicker;
